import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Crown, ShieldAlert } from "lucide-react";

const ROLE_LABEL = {
  platform_owner: "PLATFORM OWNER",
  vendor_owner: "OWNER",
  admin: "ADMIN",
  manager: "MANAGER",
  cashier: "CASHIER",
};
const ROLE_TONE = {
  platform_owner: "bg-primary text-primary-foreground hover:bg-primary",
  vendor_owner: "bg-secondary text-secondary-foreground hover:bg-secondary",
  admin: "bg-slate-800 text-white hover:bg-slate-800",
  manager: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  cashier: "bg-slate-200 text-slate-700 hover:bg-slate-200",
};

export default function Users() {
  const { user } = useAuth();
  const isPlatformOwner = user?.role === "platform_owner";
  const [rows, setRows] = useState([]);
  const load = () => api.get("/users").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.user_id}/role`, { role });
      toast.success(`${u.name} is now ${ROLE_LABEL[role] || role}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  // Options available for a given target row based on current actor + protection rules.
  const roleOptionsFor = (target) => {
    const opts = ["admin", "manager", "cashier"];
    // Only a Platform Owner can grant Owner roles.
    if (isPlatformOwner) return ["platform_owner", "vendor_owner", ...opts];
    return opts;
  };

  const isProtected = (u) =>
    u.role === "platform_owner" || u.role === "vendor_owner";

  const canEdit = (target) => {
    if (target.user_id === user.user_id) return false; // never self-edit
    if (target.role === "platform_owner" && !isPlatformOwner) return false;
    if (target.role === "vendor_owner" && !isPlatformOwner) return false;
    return true;
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">Team</div>
        <h1 className="font-display text-4xl tracking-tight" data-testid="users-title">User Management</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Roles: <span className="font-medium">Platform Owner</span>, <span className="font-medium">Vendor Owner</span>, Admin, Manager, Cashier.
          Only a Platform Owner can grant or change Owner roles.
        </p>
      </div>

      <Card className="border-border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(u => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.email}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={u.picture} />
                      <AvatarFallback>{u.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                    </Avatar>
                    <div className="text-sm flex items-center gap-1.5">
                      {u.name}
                      {isProtected(u) && <Crown className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />}
                      {u.user_id === user.user_id && <Badge className="ml-1 text-[10px]">you</Badge>}
                      {u.pending_first_login && <Badge className="ml-1 text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100">pending</Badge>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-xs">{u.email}</TableCell>
                <TableCell>
                  {!canEdit(u) ? (
                    <div className="flex items-center gap-2">
                      <Badge className={`${ROLE_TONE[u.role] || ROLE_TONE.cashier}`} data-testid={`role-locked-${u.email}`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </Badge>
                      {isProtected(u) && !isPlatformOwner && (
                        <ShieldAlert className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                      )}
                    </div>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger className="w-44" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roleOptionsFor(u).map(r => (
                          <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-ZA") : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
